import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, Check, ArrowRight, Smartphone, Search, Shield, Zap, Code, Palette } from 'lucide-react';
import { mockData } from '../mock';
import SEO from '../components/SEO';

const WebsiteServicesPage = () => {
  const { services } = mockData;
  const websiteServices = services.website;

  const additionalFeatures = [
    { icon: Smartphone, title: 'Mobil Uyumlu', description: 'Tüm cihazlarda mükemmel görünüm' },
    { icon: Search, title: 'SEO Optimizasyonu', description: 'Arama motorlarında üst sıralarda yer alma' },
    { icon: Shield, title: 'Güvenli Hosting', description: 'SSL sertifikası ve güvenlik önlemleri' },
    { icon: Zap, title: 'Hızlı Yükleme', description: 'Optimize edilmiş performans' },
    { icon: Code, title: 'Temiz Kod', description: 'Bakımı kolay, modern teknolojiler' },
    { icon: Palette, title: 'Özel Tasarım', description: 'Markanıza özel benzersiz tasarım' }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 transition-colors duration-300">
      <SEO 
        title="Web Tasarım & E-Ticaret Çözümleri"
        description="Viral Strateji ile modern, hızlı ve SEO uyumlu web siteleri geliştirin. Kurumsal web tasarım ve e-ticaret çözümlerimizle tanışın."
        keywords="web tasarım, e-ticaret sitesi, kurumsal web sitesi, seo uyumlu web tasarım, wordpress tasarım"
      />
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 dark:from-slate-800 dark:via-purple-950 dark:to-slate-900 transition-colors duration-300">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {websiteServices.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
              {websiteServices.subtitle}
            </p>
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-2.5 rounded-full font-semibold text-base hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              <span>Ücretsiz Danışmanlık</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-800 transition-colors duration-300">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {websiteServices.items.map((service, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600" />
                <div className="p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-4 sm:gap-0 mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{service.title}</h3>
                    <span className="bg-gradient-to-r from-blue-100 to-purple-100 text-blue-800 px-4 py-2 rounded-xl font-semibold text-sm self-start sm:self-auto">
                      {service.price}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <div className="bg-green-100 rounded-full p-1">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <Link
                    to="/iletisim"
                    className="block w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-2.5 px-6 rounded-full font-semibold text-sm text-center hover:from-blue-700 hover:to-purple-700 transition-all duration-300 transform group-hover:scale-105"
                  >
                    Teklif Al
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Features */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Tüm Website Projelerinde Standart
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Her website projemizde bu özelliklerin tamamı standart olarak bulunur.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 text-center">
                  <div className="bg-gradient-to-br from-blue-500 to-purple-600 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Website Geliştirme Süreci
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Projenizin başından sonuna kadar her aşamada şeffaf süreç yönetimi.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {[
                { step: 1, title: 'Planlama & Analiz', duration: '1-2 Gün', description: 'İhtiyaçlarınızı analiz ederiz ve proje planını hazırlarız.' },
                { step: 2, title: 'Tasarım & Mockup', duration: '3-5 Gün', description: 'Markanıza uygun tasarım konseptlerini geliştiririz.' },
                { step: 3, title: 'Kodlama & Geliştirme', duration: '7-10 Gün', description: 'Modern teknolojilerle kodlama sürecini tamamlarız.' },
                { step: 4, title: 'Test & Optimizasyon', duration: '2-3 Gün', description: 'Detaylı testlerle performansı optimize ederiz.' },
                { step: 5, title: 'Yayınlama & Eğitim', duration: '1 Gün', description: 'Sitenizi yayınlar ve yönetim paneli eğitimi veririz.' }
              ].map((phase, index) => (
                <div key={index} className="flex items-center space-x-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white font-bold text-xl">
                    {phase.step}
                  </div>
                  <div className="flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{phase.title}</h3>
                        <p className="text-gray-600 mt-1">{phase.description}</p>
                      </div>
                      <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                        {phase.duration}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 via-purple-600 to-pink-600 text-white">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
            Website Projenizi Başlatmaya Hazır mısınız?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
            Uzman ekibimizle görüşerek size özel website çözümü geliştirelim. Ücretsiz danışmanlık için hemen iletişime geçin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/iletisim"
              className="inline-flex items-center space-x-2 bg-white text-blue-600 px-6 py-2.5 rounded-full font-semibold text-base hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-md"
            >
              <span>Teklif Talep Et</span>
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/portfolyo"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-6 py-2.5 rounded-full font-semibold text-base hover:bg-white hover:text-blue-600 transition-all duration-300 transform hover:scale-105"
            >
              <span>Örnek Çalışmalar</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebsiteServicesPage;